import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/utils/api';

/**
 * BP6 (Codex launch audit): self-service password recovery — reset step.
 * Token arrives from the email link (?token=..&email=..). On success every
 * existing session is revoked server-side, so we route to login.
 */
const ResetPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  // BP6 audit: reset links no longer carry &email= — the user types their
  // email here; the server matches the token hash regardless.
  const emailParam = params.get('email') || '';

  const [email, setEmail] = useState(emailParam);
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const missingToken = !token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/reset-password', { email: email.trim(), token, newPassword });
      navigate('/login', { state: { resetSuccess: true } });
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Choose a new password</h1>
        {missingToken ? (
          <>
            <p className="text-gray-600 mb-6">This link is missing its reset token.</p>
            <Link to="/forgot-password" className="text-blue-600 hover:underline">Request a new reset link</Link>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                id="email" type="email" required autoComplete="email"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={email} onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">New password (min 8 characters)</label>
              <input
                id="newPassword" type="password" required minLength={8} autoComplete="new-password"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
              <input
                id="confirm" type="password" required minLength={8} autoComplete="new-password"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit" disabled={saving}
              className="w-full bg-blue-600 text-white rounded-md py-2 font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Updating…' : 'Update password'}
            </button>
            <Link to="/login" className="block text-sm text-blue-600 hover:underline">Back to sign in</Link>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
